import { SporsmalType, SvarId, Valideringstype } from '../../types/generated-types';
import { CurrentCheckboxAlternativ, CurrentCheckboxSporsmal } from '../checkbox/checkbox-types';
import { updateCurrentDatoSporsmal } from '../dato/dato-types';
import {
    addDirectVedleggToCurrent,
    addVedleggToCurrent,
    deleteVedleggFromCurrent,
    updateVedlegg,
} from '../filopplasting/filopplasting-types';
import { formatKontonummer } from '../format';
import { updateCurrentForsikringObjekterCheckboxSporsmalWithObjekter } from '../forsikring-objekter-checkbox/forsikring-objekter-checkbox-types';
import {
    isForsikringIngenAlternativerDataType,
    isForsikringSporsmal,
    updateCurrentForsikringSporsmal,
} from '../forsikring/forsikring-types';
import { isForsikringsnummerSporsmal } from '../forsikringsnummer/forsikringsnummer-types';
import { updateCurrentKontaktInfoInvolvertSporsmal } from '../kontaktInfoInvolvert/kontaktInfoInvolvert-types';
import { updateCurrentKontaktinfo } from '../kontaktinfo/kontaktinfo-types';
import { updateKredittkortnummerSvar } from '../kredittkortnummer/kredittkortnummer-types';
import { updateCurrentPostnummerAndPoststed } from '../postnummer/postnummer-types';
import { updateCurrentSimpleKontaktinfo } from '../simpleKontaktinfo/simpleKontaktinfo-types';
import {
    updateCurrentSkadedatoAndForsikringSporsmalWithSelectedAlternativ,
    updateCurrentSkadedatoAndForsikringSporsmalWithSkadedato,
} from '../skadedato-and-forsikring/skadedato-and-forsikring-types';
import { updateSkadelidtInfo, updateSkadelidtInfoHasBankIdOgMobil } from '../skadelidtInfo/skadelidtInfo-types';
import { isSkadenummerSporsmal } from '../skadenummer/skadenummer-types';
import {
    addSporsmalListSporsToCurrent,
    removeSporsmalListSporsToCurrent,
    toggleSporsmalsListExapndCard,
    updateCurrentSporsmalList,
} from '../sporsmalList/sporsmalList-types';
import { updateCurrentSporsmalSide } from '../sporsmalSide/sporsmalSide-types';
import { updateTall } from '../tall/tall-types';
import {
    addUtleggslineToCurrent,
    updateCurrentUtleggSporsmal,
    updateDatoInCurrentUtleggSporsmal,
} from '../utlegg/utlegg-types';
import {
    updateCurrectFetchVerkstederStatus,
    updateCurrentBilmerke,
    updateCurrentPostnummer,
} from '../verksted/verksted-types';
import { BesvartSporsmal, CurrentSporsmal } from './current-steg';
import {
    AddDirectVedleggToSvarAction,
    AddSporsmalListSporsmalsflytCollection,
    AddUtleggslinjeAction,
    AddVedleggToSvarAction,
    DeleteVedleggFromSvarAction,
    RemoveSporsmalListSporsmalsflytCollection,
    SporsmalsflytActionKeys,
    ToggleSporsmalListItem,
    UpdateBilmerkeAction,
    updateCurrentForsikringObjekterCheckbox,
    UpdateDatoSvarAction,
    UpdateFetchVerkstederStatusAction,
    UpdateForsikringInSkadedatoAndForsikringSvarAction,
    UpdateForsikringObjekterCheckboxAction,
    UpdateForsikringObjekterCheckboxWithObjekterAction,
    UpdateForsikringSvarAction,
    UpdateKontaktInfoIvolvertSvarAction,
    UpdateKontaktinfosvarAction,
    UpdateKredittkortnummerAction,
    UpdatePostnummerAction,
    UpdatePostnummerAndPoststedAction,
    UpdateSimpleKontaktinfosvarAction,
    UpdateSkadedatoSvarAction,
    UpdateSkadelidtInfoBankIdOgMobilSvarAction,
    UpdateSkadelidtInfoSvarAction,
    UpdateSporsmalListAction,
    UpdateSporsmalSideAction,
    UpdateSvarAction,
    UpdateSvarIdAction,
    UpdateUploadedVedleggAction,
    UpdateUtleggslinjeAction,
    UpdateUtleggslinjeDatoAction,
} from './sporsmalsflyt-actions';

const addOrReplaceAlternativCheckedStatus = (
    currentSporsmal: CurrentCheckboxSporsmal,
    markedSvarId: string,
): CurrentCheckboxAlternativ[] =>
    currentSporsmal.alternativer.map((alternativ) => {
        if (alternativ.id === markedSvarId) {
            return { ...alternativ, checked: !alternativ.checked };
        }
        return { ...alternativ };
    });

const updateCurrentSporsmal = (updatedSvar: string, currentSporsmal: CurrentSporsmal): CurrentSporsmal => {
    if (currentSporsmal.type === SporsmalType.Tekst) {
        if (currentSporsmal.sporsmal.valideringstype === Valideringstype.Kontonummer) {
            return { ...currentSporsmal, svarTekst: formatKontonummer(updatedSvar) };
        }
        return { ...currentSporsmal, svarTekst: updatedSvar };
    }
    if (currentSporsmal.type === SporsmalType.Tall) {
        return updateTall(currentSporsmal, parseFloat(updatedSvar));
    }

    if (isForsikringsnummerSporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            forsikringsnummer: updatedSvar,
        };
    }

    if (isForsikringSporsmal(currentSporsmal) && isForsikringIngenAlternativerDataType(currentSporsmal.data)) {
        return {
            ...currentSporsmal,
            data: {
                ...currentSporsmal.data,
                forsikringsnummer: updatedSvar,
            },
        };
    }

    if (isSkadenummerSporsmal(currentSporsmal)) {
        return {
            ...currentSporsmal,
            skadenummer: updatedSvar,
        };
    }
    return currentSporsmal;
};

const updateCurrentSporsmalWithSvarId = (updatedSvarId: SvarId, currentSporsmal: CurrentSporsmal): CurrentSporsmal => {
    if (currentSporsmal.type === SporsmalType.Radio) {
        return { ...currentSporsmal, svarId: updatedSvarId };
    }
    if (currentSporsmal.type === SporsmalType.Checkbox) {
        return {
            ...currentSporsmal,
            alternativer: addOrReplaceAlternativCheckedStatus(currentSporsmal, updatedSvarId),
        };
    }
    if (currentSporsmal.type === SporsmalType.Select) {
        return { ...currentSporsmal, svarId: updatedSvarId };
    }
    return currentSporsmal;
};

export type CurrentSporsmalAction =
    | UpdateSvarAction
    | UpdateSvarIdAction
    | UpdateDatoSvarAction
    | UpdateSkadedatoSvarAction
    | UpdateKontaktinfosvarAction
    | UpdateKontaktInfoIvolvertSvarAction
    | UpdateSimpleKontaktinfosvarAction
    | UpdateSkadelidtInfoSvarAction
    | UpdateSkadelidtInfoBankIdOgMobilSvarAction
    | UpdateUtleggslinjeAction
    | UpdateUtleggslinjeDatoAction
    | UpdateForsikringSvarAction
    | UpdateForsikringInSkadedatoAndForsikringSvarAction
    | AddUtleggslinjeAction
    | AddVedleggToSvarAction
    | AddDirectVedleggToSvarAction
    | DeleteVedleggFromSvarAction
    | UpdatePostnummerAction
    | UpdatePostnummerAndPoststedAction
    | UpdateBilmerkeAction
    | UpdateFetchVerkstederStatusAction
    | UpdateSporsmalSideAction
    | UpdateSporsmalListAction
    | AddSporsmalListSporsmalsflytCollection
    | RemoveSporsmalListSporsmalsflytCollection
    | ToggleSporsmalListItem
    | UpdateUploadedVedleggAction
    | UpdateForsikringObjekterCheckboxAction
    | UpdateForsikringObjekterCheckboxWithObjekterAction
    | UpdateKredittkortnummerAction
    | never;

export const currentSporsmalReducer = (
    currentSporsmal: CurrentSporsmal,
    besvarteSporsmalFromOutsideCollection: BesvartSporsmal[],
    action: CurrentSporsmalAction = {} as CurrentSporsmalAction,
): CurrentSporsmal => {
    switch (action.type) {
        case SporsmalsflytActionKeys.UpdateSvarTekst:
            return updateCurrentSporsmal(action.svarTekst, currentSporsmal);

        case SporsmalsflytActionKeys.UpdateSvarId:
            return updateCurrentSporsmalWithSvarId(action.svarId, currentSporsmal);

        case SporsmalsflytActionKeys.UpdateDatoSvar:
            return updateCurrentDatoSporsmal(currentSporsmal, action.dato);

        case SporsmalsflytActionKeys.UpdateSkadedatoInSkadedatoAndForsikringSvar:
            return updateCurrentSkadedatoAndForsikringSporsmalWithSkadedato(
                currentSporsmal,
                action.kundeinfo,
                action.skadedato,
            );

        case SporsmalsflytActionKeys.UpdateKontaktinfosvar:
            return updateCurrentKontaktinfo(currentSporsmal, action.felt, action.svarTekst);

        case SporsmalsflytActionKeys.UpdateKontaktInfoInvolvertSvar:
            return updateCurrentKontaktInfoInvolvertSporsmal(currentSporsmal, action.felt, action.svarTekst);

        case SporsmalsflytActionKeys.UpdateSimpleKontaktinfosvar:
            return updateCurrentSimpleKontaktinfo(currentSporsmal, action.felt, action.svarTekst);

        case SporsmalsflytActionKeys.UpdateSkadelidtInfoSvar:
            return updateSkadelidtInfo(currentSporsmal, action.field, action.svar);

        case SporsmalsflytActionKeys.UpdateSkadelidtInfoBankIdOgMobilSvar:
            return updateSkadelidtInfoHasBankIdOgMobil(currentSporsmal, action.svar);

        case SporsmalsflytActionKeys.UpdateUtleggslinje:
            return updateCurrentUtleggSporsmal(currentSporsmal, action.utleggslinje);

        case SporsmalsflytActionKeys.UpdateUtleggslinjeDato:
            return updateDatoInCurrentUtleggSporsmal(currentSporsmal, action.utleggslinjeId, action.dato);

        case SporsmalsflytActionKeys.UpdateForsikringSvar:
            return updateCurrentForsikringSporsmal(currentSporsmal, action.selectedAlternativ);

        case SporsmalsflytActionKeys.UpdateForsikringInSkadedatoAndForsikringSvar:
            return updateCurrentSkadedatoAndForsikringSporsmalWithSelectedAlternativ(
                currentSporsmal,
                action.selectedAlternativ,
            );

        case SporsmalsflytActionKeys.AddUtleggslinje:
            return addUtleggslineToCurrent(currentSporsmal);

        case SporsmalsflytActionKeys.UpdateUploadVedlegg:
            return updateVedlegg(currentSporsmal, action.result, action.status);

        case SporsmalsflytActionKeys.AddVedleggToSvar:
            return addVedleggToCurrent(currentSporsmal, action.vedlegg);

        case SporsmalsflytActionKeys.AddDirectVedleggToSvar:
            return addDirectVedleggToCurrent(currentSporsmal, action.vedlegg, action.status);

        case SporsmalsflytActionKeys.DeleteVedleggFromSvar:
            return deleteVedleggFromCurrent(currentSporsmal, action.vedlegg);

        case SporsmalsflytActionKeys.UpdatePostnummer:
            return updateCurrentPostnummer(currentSporsmal, action.postnummer);

        case SporsmalsflytActionKeys.UpdateBilmerke:
            return updateCurrentBilmerke(currentSporsmal, action.bilmerke);

        case SporsmalsflytActionKeys.UpdateFetchVerkstederStatus:
            return updateCurrectFetchVerkstederStatus(
                currentSporsmal,
                action.verksteder,
                action.postnummer,
                action.bilmerke,
            );

        case SporsmalsflytActionKeys.UpdatePostnummerAndPoststed:
            return updateCurrentPostnummerAndPoststed(currentSporsmal, action.postnummer, action.poststed);

        case SporsmalsflytActionKeys.UpdateSporsmalSide:
            return updateCurrentSporsmalSide(currentSporsmal, action, besvarteSporsmalFromOutsideCollection);

        case SporsmalsflytActionKeys.UpdateSporsmalList:
            return updateCurrentSporsmalList(currentSporsmal, action, besvarteSporsmalFromOutsideCollection);

        case SporsmalsflytActionKeys.AddSporsmalListSporsmalsflytCollection:
            return addSporsmalListSporsToCurrent(currentSporsmal, besvarteSporsmalFromOutsideCollection);

        case SporsmalsflytActionKeys.RemoveSporsmalListSporsmalsflytCollection:
            return removeSporsmalListSporsToCurrent(currentSporsmal, action.id);

        case SporsmalsflytActionKeys.ToggleSporsmalListItem:
            return toggleSporsmalsListExapndCard(currentSporsmal, action.id);

        case SporsmalsflytActionKeys.UpdateForsikringObjekterValg:
            return updateCurrentForsikringObjekterCheckbox(currentSporsmal, action.valgtObjektnummer);

        case SporsmalsflytActionKeys.UpdateForsikringObjekterCheckboxWithObjekter:
            return updateCurrentForsikringObjekterCheckboxSporsmalWithObjekter(currentSporsmal, action.objekter);

        case SporsmalsflytActionKeys.UpdateKredittkortnummer:
            return updateKredittkortnummerSvar(currentSporsmal, action.updatedSvar, action.inputGroup);

        default:
            return currentSporsmal;
    }
};
